import { DeleteOutlined, DownOutlined, EditOutlined, FilePdfOutlined, FolderOutlined, RightOutlined, TableOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Popconfirm, Select, Spin, Upload, message } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { DocumentProjectFileModel, ProjectListModel, deleteProjectFile, getFileViewUrl, getMyProjects, getProjectFiles, updateProjectFile } from '../../api/documentProjectApi';
import PermissionService from '../../services/Permissions';
import { canRoleDeleteDocuments, canRoleManageDocuments } from '../../config/rolePermissions';
import { getLocalUser, isLocallyAuthenticated, useLocalAuth } from '../../auth/LocalAuthService';
import PageBanner from '../../components/shared/PageBanner';

const { Option } = Select;

interface EditState {
    file: DocumentProjectFileModel;
    projectId: number;
    title: string;
    kind: 'pdf' | 'spreadsheet';
    newFile: File | null;
    saving: boolean;
}

interface ProjectWithFiles extends ProjectListModel {
    files: DocumentProjectFileModel[];
    loading: boolean;
}

const DocumentsList: FunctionComponent = () => {
    const { t } = useTranslation(['f', 'b']);
    const history = useHistory();
    const localAuth = useLocalAuth();
    const localRole = Number(getLocalUser()?.role ?? -1);
    const isLocalAdmin = isLocallyAuthenticated() ? canRoleManageDocuments(localRole) : false;
    const isLocalEditDelete = isLocallyAuthenticated() ? canRoleDeleteDocuments(localRole) : false;
    const isMsalAdmin = PermissionService.canViewAdminPanelMenu();
    const isAdmin = localAuth ? isLocalAdmin : isMsalAdmin;
    const canEditDelete = localAuth ? isLocalEditDelete : isMsalAdmin;

    const [projects, setProjects] = useState<ProjectWithFiles[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [editState, setEditState] = useState<EditState | null>(null);

    const toggleProject = (id: number) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const preventContextMenu = (e: React.MouseEvent) => e.preventDefault();

    useEffect(() => {
        setLoadingProjects(true);
        getMyProjects()
            .then((list) => {
                const initial: ProjectWithFiles[] = list.map((p) => ({ ...p, files: [], loading: true }));
                setProjects(initial);
                setLoadingProjects(false);

                list.forEach((p) => {
                    getProjectFiles(p.id)
                        .then((files) => {
                            setProjects((prev) =>
                                prev.map((proj) =>
                                    proj.id === p.id ? { ...proj, files, loading: false } : proj,
                                ),
                            );
                        })
                        .catch(() => {
                            setProjects((prev) =>
                                prev.map((proj) =>
                                    proj.id === p.id ? { ...proj, loading: false } : proj,
                                ),
                            );
                        });
                });
            })
            .catch(() => setLoadingProjects(false));
    }, []);

    const openEdit = (projectId: number, file: DocumentProjectFileModel) => {
        setEditState({ file, projectId, title: file.title, kind: file.kind as 'pdf' | 'spreadsheet', newFile: null, saving: false });
    };

    const handleEditSave = async () => {
        if (!editState) return;
        if (!editState.title.trim()) { message.warning('Title cannot be empty.'); return; }
        setEditState((s) => ({ ...s, saving: true }));
        try {
            const updated = await updateProjectFile(editState.file.id, editState.title.trim(), editState.kind, editState.newFile);
            setProjects((prev) =>
                prev.map((proj) =>
                    proj.id === editState.projectId
                        ? { ...proj, files: proj.files.map((f) => (f.id === updated.id ? updated : f)) }
                        : proj,
                ),
            );
            message.success('File updated.');
            setEditState(null);
        } catch {
            message.error('Failed to update file.');
            setEditState((s) => ({ ...s, saving: false }));
        }
    };

    const handleDeleteFile = async (projectId: number, fileId: number) => {
        try {
            await deleteProjectFile(fileId);
            setProjects((prev) =>
                prev.map((proj) =>
                    proj.id === projectId
                        ? { ...proj, files: proj.files.filter((f) => f.id !== fileId) }
                        : proj,
                ),
            );
            message.success('File deleted.');
        } catch {
            message.error('Failed to delete file.');
        }
    };

    const handleFileClick = async (file: DocumentProjectFileModel) => {
        try {
            const sasUrl = await getFileViewUrl(file.id);
            const viewerUrl =
                file.kind === 'spreadsheet'
                    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                          sasUrl,
                      )}&wdDownloadButton=False&wdAllowInteractivity=False`
                    : sasUrl;
            history.push(`/documents/project-file/${file.id}`, {
                title: file.title,
                viewerUrl,
                blobUrl: sasUrl,
                kind: file.kind,
            });
        } catch {
            message.error('Could not open document. Please try again.');
        }
    };

    return (
        <div className="documents-page documents-page--with-app-header" onContextMenu={preventContextMenu}>
            <div className="documents-page__inner">
                {canEditDelete ? (
                    <div className="documents-page__section-card">
                        <PageBanner title="Manage Deal Room" storageKey="banner-manage-documents">
                            <div className="documents-page__subtitle">
                                Upload and organise files under each project.{' '}
                                <button
                                    type="button"
                                    className="documents-page__toolbar-btn"
                                    style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                    onClick={() => history.push('/manage-documents')}
                                >
                                    Open full manage view
                                </button>
                            </div>
                        </PageBanner>
                    </div>
                ) : null}

                <div className="documents-page__section-card">
                    <PageBanner title={t('f:documents.pageTitle')} storageKey="banner-documents">
                        <div className="documents-page__subtitle">{t('f:documents.pageSubtitle')}</div>
                    </PageBanner>
                </div>

                <div className="documents-page__section-card" style={{ marginTop: '2.5rem' }}>
                    {loadingProjects ? (
                        <div style={{ padding: '40px 0', textAlign: 'center' }}>
                            <Spin />
                        </div>
                    ) : projects.length === 0 ? (
                        <div style={{ color: '#6b7280', padding: '24px 0' }}>No documents available.</div>
                    ) : (
                        <div style={{ display: 'grid', gap: 8 }}>
                            {projects.map((project) => {
                                const isOpen = expandedIds.has(project.id);
                                return (
                                    <div key={project.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                                        {/* Accordion header */}
                                        <button
                                            type="button"
                                            onClick={() => toggleProject(project.id)}
                                            style={{
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                padding: '14px 16px',
                                                background: isOpen ? '#f0f4ff' : '#fff',
                                                border: 'none',
                                                borderBottom: isOpen ? '1px solid #e5e7eb' : 'none',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                transition: 'background 0.15s',
                                            }}
                                        >
                                            <FolderOutlined style={{ color: '#2563eb', fontSize: 18, flexShrink: 0 }} />
                                            <span style={{ fontWeight: 600, fontSize: 15, color: '#111827', flex: 1 }}>
                                                {project.name}
                                            </span>
                                            <span style={{ fontSize: 12, color: '#9ca3af', marginRight: 8 }}>
                                                {project.loading
                                                    ? '…'
                                                    : `${project.files.length} file${project.files.length !== 1 ? 's' : ''}`}
                                            </span>
                                            <DownOutlined
                                                style={{
                                                    fontSize: 12,
                                                    color: '#6b7280',
                                                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s',
                                                    flexShrink: 0,
                                                }}
                                            />
                                        </button>

                                        {/* Accordion body */}
                                        {isOpen && (
                                            <div style={{ padding: '12px 16px' }}>
                                                {project.loading ? (
                                                    <div style={{ padding: '12px 0' }}>
                                                        <Spin size="small" />
                                                    </div>
                                                ) : project.files.length === 0 ? (
                                                    <div style={{ color: '#9ca3af', fontSize: 13, padding: '8px 0' }}>
                                                        No files in this project.
                                                    </div>
                                                ) : (
                                                    <div className="documents-page__list" role="list">
                                                        {project.files.map((file) => {
                                                            const Icon = file.kind === 'pdf' ? FilePdfOutlined : TableOutlined;
                                                            return (
                                                                <div
                                                                    key={file.id}
                                                                    className="documents-page__list-item"
                                                                    role="listitem"
                                                                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        className="documents-page__card"
                                                                        onClick={() => handleFileClick(file)}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <span className="documents-page__card-icon" aria-hidden>
                                                                            <Icon />
                                                                        </span>
                                                                        <span className="documents-page__card-body">
                                                                            <span className="documents-page__card-title">
                                                                                {file.title}
                                                                            </span>
                                                                            <span className="documents-page__card-meta">
                                                                                {file.kind === 'pdf'
                                                                                    ? t('f:documents.kindPdf')
                                                                                    : t('f:documents.kindSpreadsheet')}
                                                                            </span>
                                                                        </span>
                                                                        <RightOutlined className="documents-page__card-chevron" />
                                                                    </button>
                                                                    {canEditDelete && (
                                                                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                                                            <button
                                                                                type="button"
                                                                                title="Edit file"
                                                                                onClick={() => openEdit(project.id, file)}
                                                                                style={{
                                                                                    background: 'none',
                                                                                    border: '1px solid #93c5fd',
                                                                                    borderRadius: 6,
                                                                                    color: '#2563eb',
                                                                                    padding: '6px 8px',
                                                                                    cursor: 'pointer',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                }}
                                                                            >
                                                                                <EditOutlined />
                                                                            </button>
                                                                            <Popconfirm
                                                                                title="Delete this file?"
                                                                                description="This action cannot be undone."
                                                                                onConfirm={() => handleDeleteFile(project.id, file.id)}
                                                                                okText="Delete"
                                                                                okButtonProps={{ danger: true }}
                                                                                cancelText="Cancel"
                                                                            >
                                                                                <button
                                                                                    type="button"
                                                                                    title="Delete file"
                                                                                    style={{
                                                                                        background: 'none',
                                                                                        border: '1px solid #fca5a5',
                                                                                        borderRadius: 6,
                                                                                        color: '#ef4444',
                                                                                        padding: '6px 8px',
                                                                                        cursor: 'pointer',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                    }}
                                                                                >
                                                                                    <DeleteOutlined />
                                                                                </button>
                                                                            </Popconfirm>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

            </div>

            {/* Edit modal */}
            <Modal
                title="Edit Document"
                visible={!!editState}
                onOk={handleEditSave}
                onCancel={() => setEditState(null)}
                confirmLoading={editState?.saving}
                okText="Save"
                destroyOnClose
            >
                {editState && (
                    <div style={{ display: 'grid', gap: 16, paddingTop: 8 }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#374151' }}>Document Title</label>
                            <Input
                                value={editState.title}
                                onChange={(e) => setEditState((s) => ({ ...s, title: e.target.value }))}
                                placeholder="Enter document title…"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#374151' }}>File Type</label>
                            <Select
                                style={{ width: 200 }}
                                value={editState.kind}
                                onChange={(val) => setEditState((s) => ({ ...s, kind: val, newFile: null }))}
                            >
                                <Option value="pdf">PDF</Option>
                                <Option value="spreadsheet">Spreadsheet (Excel)</Option>
                            </Select>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#374151' }}>
                                Replace File <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional — leave empty to keep existing)</span>
                            </label>
                            <Upload
                                beforeUpload={(file) => {
                                    const ext = file.name.split('.').pop()?.toLowerCase();
                                    if (editState.kind === 'pdf' && ext !== 'pdf') {
                                        message.error('Only PDF files are allowed for this type.');
                                        return Upload.LIST_IGNORE;
                                    }
                                    if (editState.kind === 'spreadsheet' && ext !== 'xls' && ext !== 'xlsx') {
                                        message.error('Only Excel files (.xls, .xlsx) are allowed for this type.');
                                        return Upload.LIST_IGNORE;
                                    }
                                    setEditState((s) => ({ ...s, newFile: file }));
                                    return false;
                                }}
                                onRemove={() => setEditState((s) => ({ ...s, newFile: null }))}
                                maxCount={1}
                                accept={editState.kind === 'pdf' ? '.pdf' : '.xls,.xlsx'}
                                fileList={editState.newFile ? [{ uid: '-1', name: editState.newFile.name, status: 'done' }] : []}
                            >
                                <Button icon={<UploadOutlined />}>
                                    {editState.kind === 'pdf' ? 'Select PDF' : 'Select Excel File'}
                                </Button>
                            </Upload>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default DocumentsList;
